// Reading a question set from the file a person already has: a text or
// Markdown file, or a sheet with a question column and an answer column. The
// expected answer is the grading key, so every shape that carries one keeps it.
//
// Text shapes, tried in this order:
//   1. `**Q1. question**` header lines, the answer in the lines that follow.
//   2. `Q:` / `A:` lines (also `Question:` / `Answer:`, any case, numbered or
//      not). An answer runs until a blank line or the next question.
//   3. Alternating paragraphs: a question ending in `?`, a blank line, its
//      answer, a blank line, and so on through the whole file.
//   4. One question per line, with no answers.
// A Markdown heading that mentions "ontology" marks the questions under it as
// ontology questions; any other heading marks data questions.
import type { EvalQuestion, QuestionType } from "@/lib/api";

const QA_HEADER_RE = /^\*\*\s*Q\d+[.)]?\s*(.*?)\*\*$/i;
const LIST_MARK_RE = /^(?:[-*+]\s+|\d+[.)]\s+|>\s+)/;
const Q_LINE_RE = /^(?:[-*+]\s+|\d+[.)]\s+)?\**\s*(?:q|question)\s*\d*\s*[:]\s*\**\s*(.*)$/i;
const A_LINE_RE =
  /^(?:[-*+]\s+)?\**\s*(?:a|answer|expected answer)\s*\d*\s*[:]\s*\**\s*(.*)$/i;

const isHr = (l: string) => /^([-*_])\1{2,}$/.test(l);

function headingKind(l: string): QuestionType | null {
  const m = l.match(/^#{1,6}\s+(.*)$/);
  if (!m) return null;
  return (m[1] ?? "").toLowerCase().includes("ontolog") ? "ontology" : "data";
}

function question(type: QuestionType, text: string, expected = ""): EvalQuestion {
  return { id: "", type, text, expected_answer: expected, unanswerable: false };
}

function parseHeaderBlocks(lines: string[]): EvalQuestion[] {
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  let cur: EvalQuestion | null = null;
  const flush = () => {
    if (cur) {
      cur.expected_answer = cur.expected_answer.trim();
      if (cur.text) out.push(cur);
      cur = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || isHr(line)) continue;
    const header = line.match(QA_HEADER_RE);
    if (header) {
      flush();
      cur = question(kind, (header[1] ?? "").trim());
      continue;
    }
    const hk = headingKind(line);
    if (hk) {
      flush();
      kind = hk;
      continue;
    }
    if (cur) cur.expected_answer += (cur.expected_answer ? " " : "") + line;
  }
  flush();
  return out;
}

function parseQaLines(lines: string[]): EvalQuestion[] {
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  let cur: EvalQuestion | null = null;
  // What a line with no Q:/A: marker continues: the question, the answer, or nothing.
  let field: "q" | "a" | null = null;
  const flush = () => {
    if (cur) {
      cur.text = cur.text.trim();
      cur.expected_answer = cur.expected_answer.trim();
      if (cur.text) out.push(cur);
      cur = null;
    }
    field = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (field === "a") field = null;
      continue;
    }
    if (isHr(line)) continue;
    const hk = headingKind(line);
    if (hk) {
      flush();
      kind = hk;
      continue;
    }
    const q = line.match(Q_LINE_RE);
    if (q) {
      flush();
      cur = question(kind, q[1] ?? "");
      field = "q";
      continue;
    }
    const a = line.match(A_LINE_RE);
    if (a && cur) {
      cur.expected_answer += (cur.expected_answer ? "\n" : "") + (a[1] ?? "");
      field = "a";
      continue;
    }
    if (!cur) continue;
    if (field === "q") cur.text += " " + line;
    else if (field === "a") cur.expected_answer += "\n" + line;
  }
  flush();
  return out;
}

// Paragraphs of a file, a heading standing as a paragraph of its own.
function paragraphs(lines: string[]): string[][] {
  const out: string[][] = [];
  let cur: string[] = [];
  const end = () => {
    if (cur.length > 0) out.push(cur);
    cur = [];
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || isHr(line)) {
      end();
      continue;
    }
    if (headingKind(line)) {
      end();
      out.push([line]);
      continue;
    }
    cur.push(line);
  }
  end();
  return out;
}

// Question paragraph, answer paragraph, in turn, under each heading. Only when
// every question paragraph is one line ending in `?` and no answer does, so a
// list of questions split by blank lines is never read as questions and answers.
function parseAlternating(lines: string[]): EvalQuestion[] | null {
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  let pending: string | null = null;
  for (const para of paragraphs(lines)) {
    const first = para[0] ?? "";
    const hk = para.length === 1 ? headingKind(first) : null;
    if (hk) {
      if (pending !== null) return null;
      kind = hk;
      continue;
    }
    const text = para.join("\n");
    if (pending === null) {
      if (para.length !== 1 || !first.endsWith("?")) return null;
      pending = first.replace(LIST_MARK_RE, "").trim();
    } else {
      if (para.some((l) => l.endsWith("?"))) return null;
      out.push(question(kind, pending, text));
      pending = null;
    }
  }
  if (pending !== null || out.length === 0) return null;
  return out;
}

function parseOnePerLine(lines: string[]): EvalQuestion[] {
  const out: EvalQuestion[] = [];
  let kind: QuestionType = "data";
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || isHr(line)) continue;
    const hk = headingKind(line);
    if (hk) {
      kind = hk;
      continue;
    }
    const text = line.replace(LIST_MARK_RE, "").trim();
    if (text) out.push(question(kind, text));
  }
  return out;
}

export function parseQuestionsFile(text: string): EvalQuestion[] {
  const lines = text.split(/\r?\n/);
  if (lines.some((l) => QA_HEADER_RE.test(l.trim()))) return parseHeaderBlocks(lines);
  if (lines.some((l) => Q_LINE_RE.test(l.trim()))) return parseQaLines(lines);
  return parseAlternating(lines) ?? parseOnePerLine(lines);
}

const QUESTION_HEADER_RE = /question/i;
const ANSWER_HEADER_RE = /answer|expected/i;
const TYPE_HEADER_RE = /^\s*(?:type|kind|question type)\s*$/i;

/** Questions from a table: a column headed Question and one headed Answer (or
 *  Expected answer); a column headed Type marks ontology questions. A table
 *  whose header names no question column is read as question, answer in the
 *  first two columns, the header row counted as the first question. */
export function questionsFromTable(columns: string[], rows: string[][]): EvalQuestion[] {
  let qi = columns.findIndex((c) => QUESTION_HEADER_RE.test(c) && !ANSWER_HEADER_RE.test(c));
  let ai = columns.findIndex((c) => ANSWER_HEADER_RE.test(c));
  const ti = columns.findIndex((c) => TYPE_HEADER_RE.test(c));
  let body = rows;
  if (qi < 0) {
    qi = 0;
    ai = columns.length > 1 ? 1 : -1;
    body = [columns, ...rows];
  }
  const out: EvalQuestion[] = [];
  for (const row of body) {
    const text = (row[qi] ?? "").trim();
    if (!text) continue;
    const kind: QuestionType =
      ti >= 0 && (row[ti] ?? "").toLowerCase().includes("ontolog") ? "ontology" : "data";
    out.push(question(kind, text, ai >= 0 ? (row[ai] ?? "").trim() : ""));
  }
  return out;
}

/** The file kinds the bank import reads through the spreadsheet reader. */
export function isSheetFile(name: string): boolean {
  return /\.(csv|xlsx|xls|xlsm|ods)$/i.test(name);
}
